// src/features/dashboard/components/ManagerDashboard/ProductivityReport/ProductivityFilters.tsx

import {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ChevronDown,
  Search,
  X,
} from 'lucide-react';

interface ProductivityFiltersProps {
  teams: string[];
  locations: string[];
  shifts: string[];

  selectedTeam: string;
  selectedLocation: string;
  selectedShift: string;

  search: string;

  onTeamChange: (
    team: string,
  ) => void;

  onLocationChange: (
    location: string,
  ) => void;

  onShiftChange: (
    shift: string,
  ) => void;

  onSearchChange: (
    search: string,
  ) => void;
}

interface FilterDropdownProps {
  label: string;
  value: string;
  options: string[];
  placeholder: string;

  onChange: (
    value: string,
  ) => void;
}

const FilterDropdown = ({
  label,
  value,
  options,
  placeholder,
  onChange,
}: FilterDropdownProps) => {

  const [
    open,
    setOpen,
  ] = useState(false);

  const [
    search,
    setSearch,
  ] = useState('');

  const dropdownRef =
    useRef<HTMLDivElement>(null);

  // =========================================================
  // CLOSE DROPDOWN WHEN CLICKING OUTSIDE
  // =========================================================

  useEffect(() => {

    const handleClickOutside = (
      event: MouseEvent,
    ) => {

      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(
          event.target as Node,
        )
      ) {

        setOpen(false);

        setSearch('');
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside,
    );

    return () => {

      document.removeEventListener(
        'mousedown',
        handleClickOutside,
      );
    };

  }, []);

  // =========================================================
  // FILTER OPTIONS
  // =========================================================

  const filteredOptions =
    options.filter(
      (option) =>
        option
          .toLowerCase()
          .includes(
            search.toLowerCase(),
          ),
    );

  // =========================================================
  // SELECT OPTION
  // =========================================================

  const handleSelect = (
    option: string,
  ) => {

    onChange(option);

    setOpen(false);

    setSearch('');
  };

  // =========================================================
  // DROPDOWN
  // =========================================================

  return (

    <div
      ref={dropdownRef}
      className="
        relative
        w-[180px]
        shrink-0

        /* ======================================================
           110% ZOOM
        ======================================================= */

        [@media(max-width:1100px)]:w-[155px]

        /* ======================================================
           125% - 150% ZOOM
        ======================================================= */

        [@media(max-width:1000px)]:w-[140px]
      "
    >

      {/* ======================================================
          LABEL
      ======================================================= */}

      <span
        className="
          mb-1
          block
          text-[10px]
          font-medium
          leading-3
          text-slate-500

          [@media(max-width:1100px)]:text-[9px]

          [@media(max-width:1000px)]:mb-0.5
          [@media(max-width:1000px)]:text-[8px]
        "
      >
        {label}
      </span>


      {/* ======================================================
          SELECTED VALUE
      ======================================================= */}

      <button
        type="button"
        onClick={() =>
          setOpen(
            (prev) => !prev,
          )
        }
        className="
          flex
          h-9
          w-full
          min-w-0
          items-center
          justify-between

          rounded-lg

          border
          border-slate-300

          bg-white

          px-3

          text-left
          text-[12px]
          text-slate-700

          outline-none
          transition

          hover:border-slate-400

          focus:border-blue-400
          focus:ring-2
          focus:ring-blue-100

          [@media(max-width:1100px)]:h-8
          [@media(max-width:1100px)]:rounded-md
          [@media(max-width:1100px)]:px-2.5
          [@media(max-width:1100px)]:text-[10px]

          [@media(max-width:1000px)]:h-7
          [@media(max-width:1000px)]:px-2
          [@media(max-width:1000px)]:text-[9px]
        "
      >

        <span
          className="
            min-w-0
            flex-1
            truncate
          "
          title={
            value ||
            placeholder
          }
        >
          {value ||
            placeholder}
        </span>

        <ChevronDown
          size={14}
          className={`
            ml-1
            shrink-0
            text-slate-400
            transition

            ${
              open
                ? 'rotate-180'
                : ''
            }

            [@media(max-width:1100px)]:h-[13px]
            [@media(max-width:1100px)]:w-[13px]

            [@media(max-width:1000px)]:h-[12px]
            [@media(max-width:1000px)]:w-[12px]
          `}
        />

      </button>


      {/* ======================================================
          DROPDOWN
      ======================================================= */}

      {open && (

        <div
          className="
            absolute
            left-0
            top-[58px]
            z-50
            w-full
            overflow-hidden

            rounded-lg

            border
            border-slate-200

            bg-white

            shadow-lg

            [@media(max-width:1100px)]:top-[51px]
            [@media(max-width:1100px)]:rounded-md

            [@media(max-width:1000px)]:top-[44px]
          "
        >

          {/* ==================================================
              SEARCH INSIDE DROPDOWN
          =================================================== */}

          <div
            className="
              border-b
              border-slate-100
              p-2

              [@media(max-width:1100px)]:p-1.5

              [@media(max-width:1000px)]:p-1
            "
          >

            <div
              className="
                relative
              "
            >

              <Search
                size={13}
                className="
                  absolute
                  left-2.5
                  top-1/2
                  -translate-y-1/2
                  text-slate-400

                  [@media(max-width:1000px)]:left-2
                  [@media(max-width:1000px)]:h-[11px]
                  [@media(max-width:1000px)]:w-[11px]
                "
              />

              <input
                value={search}
                onChange={(e) =>
                  setSearch(
                    e.target.value,
                  )
                }
                placeholder="Search..."
                autoFocus
                className="
                  h-8
                  w-full
                  rounded-md

                  border
                  border-blue-300

                  bg-white

                  pl-8
                  pr-2

                  text-[11px]
                  text-slate-700

                  outline-none

                  ring-2
                  ring-blue-100

                  placeholder:text-slate-400

                  [@media(max-width:1100px)]:h-7
                  [@media(max-width:1100px)]:pl-7
                  [@media(max-width:1100px)]:text-[10px]

                  [@media(max-width:1000px)]:h-6
                  [@media(max-width:1000px)]:pl-6
                  [@media(max-width:1000px)]:text-[9px]
                "
              />

            </div>

          </div>


          {/* ==================================================
              OPTIONS
          =================================================== */}

          <div
            className="
              max-h-[140px]
              overflow-y-auto
              p-1

              [@media(max-width:1100px)]:max-h-[125px]

              [@media(max-width:1000px)]:max-h-[110px]
            "
          >

            {filteredOptions.length ===
            0 ? (

              <div
                className="
                  px-3
                  py-3
                  text-center
                  text-[11px]
                  text-slate-400

                  [@media(max-width:1100px)]:px-2
                  [@media(max-width:1100px)]:py-2
                  [@media(max-width:1100px)]:text-[10px]

                  [@media(max-width:1000px)]:px-1.5
                  [@media(max-width:1000px)]:py-2
                  [@media(max-width:1000px)]:text-[9px]
                "
              >
                No results found
              </div>

            ) : (

              filteredOptions.map(
                (option) => {

                  const isSelected =
                    option === value;

                  return (

                    <button
                      key={option}
                      type="button"
                      onClick={() =>
                        handleSelect(
                          option,
                        )
                      }
                      className={`
                        block
                        w-full

                        rounded-md

                        px-3
                        py-2

                        text-left
                        text-[11px]

                        transition

                        [@media(max-width:1100px)]:px-2.5
                        [@media(max-width:1100px)]:py-1.5
                        [@media(max-width:1100px)]:text-[10px]

                        [@media(max-width:1000px)]:px-2
                        [@media(max-width:1000px)]:py-1.5
                        [@media(max-width:1000px)]:text-[9px]

                        ${
                          isSelected
                            ? `
                              bg-[#EEF0FF]
                              font-medium
                              text-slate-800
                            `
                            : `
                              text-slate-700
                              hover:bg-slate-50
                            `
                        }
                      `}
                    >
                      <span
                        className="
                          block
                          whitespace-normal
                          break-words
                        "
                      >
                        {option}
                      </span>
                    </button>

                  );
                },
              )

            )}

          </div>

        </div>

      )}

    </div>
  );
};


// ============================================================================
// PRODUCTIVITY FILTERS
// ============================================================================

const ProductivityFilters = ({
  teams,
  locations,
  shifts,

  selectedTeam,
  selectedLocation,
  selectedShift,

  search,

  onTeamChange,
  onLocationChange,
  onShiftChange,
  onSearchChange,
}: ProductivityFiltersProps) => {

  return (

    <div
      className="
        rounded-2xl
        border
        border-slate-200
        bg-white

        p-4

        [@media(max-width:1100px)]:rounded-xl
        [@media(max-width:1100px)]:p-3

        [@media(max-width:1000px)]:rounded-lg
        [@media(max-width:1000px)]:p-2.5
      "
    >

      <div
        className="
          flex
          w-full
          flex-wrap
          items-end

          gap-3

          /* ======================================================
             Keep filters in one row on desktop
          ======================================================= */

          lg:flex-nowrap

          [@media(max-width:1100px)]:gap-2

          [@media(max-width:1000px)]:gap-1.5

          [@media(min-width:900px)]:flex-nowrap
        "
      >

        {/* ========================================================
            TEAM
        ========================================================= */}

        <FilterDropdown
          label="Team"
          value={
            selectedTeam
          }
          options={
            teams
          }
          placeholder="All Teams"
          onChange={
            onTeamChange
          }
        />


        {/* ========================================================
            ASSOCIATE SEARCH
        ========================================================= */}

        <div
          className="
            w-[220px]
            shrink-0

            [@media(max-width:1100px)]:w-[185px]

            [@media(max-width:1000px)]:w-[165px]
          "
        >

          <span
            className="
              mb-1
              block
              text-[10px]
              font-medium
              leading-3
              text-slate-500

              [@media(max-width:1100px)]:text-[9px]

              [@media(max-width:1000px)]:mb-0.5
              [@media(max-width:1000px)]:text-[8px]
            "
          >
            Associate
          </span>


          <div
            className="
              relative
            "
          >

            <Search
              size={14}
              className="
                absolute
                left-3
                top-1/2
                -translate-y-1/2
                text-slate-400

                [@media(max-width:1100px)]:left-2.5
                [@media(max-width:1100px)]:h-[13px]
                [@media(max-width:1100px)]:w-[13px]

                [@media(max-width:1000px)]:left-2
                [@media(max-width:1000px)]:h-[12px]
                [@media(max-width:1000px)]:w-[12px]
              "
            />

            <input
              value={
                search
              }
              onChange={(
                e,
              ) =>
                onSearchChange(
                  e.target.value,
                )
              }
              placeholder="Search associate..."
              className="
                h-9
                w-full

                rounded-lg

                border
                border-slate-300

                bg-white

                pl-9
                pr-8

                text-[12px]
                text-slate-700

                outline-none

                transition

                placeholder:text-slate-400

                focus:border-blue-400
                focus:ring-2
                focus:ring-blue-100

                [@media(max-width:1100px)]:h-8
                [@media(max-width:1100px)]:rounded-md
                [@media(max-width:1100px)]:pl-8
                [@media(max-width:1100px)]:pr-7
                [@media(max-width:1100px)]:text-[10px]

                [@media(max-width:1000px)]:h-7
                [@media(max-width:1000px)]:pl-7
                [@media(max-width:1000px)]:pr-6
                [@media(max-width:1000px)]:text-[9px]
              "
            />


            {/* ==================================================
                CLEAR SEARCH
            =================================================== */}

            {search && (

              <button
                type="button"
                onClick={() =>
                  onSearchChange(
                    '',
                  )
                }
                className="
                  absolute
                  right-2
                  top-1/2
                  -translate-y-1/2

                  rounded-full

                  p-1

                  text-slate-400

                  hover:bg-slate-100
                  hover:text-slate-600

                  [@media(max-width:1000px)]:right-1
                  [@media(max-width:1000px)]:p-0.5
                "
              >

                <X
                  size={13}
                  className="
                    [@media(max-width:1000px)]:h-[11px]
                    [@media(max-width:1000px)]:w-[11px]
                  "
                />

              </button>

            )}

          </div>

        </div>


        {/* ========================================================
            LOCATION
        ========================================================= */}

        <FilterDropdown
          label="Location"
          value={
            selectedLocation
          }
          options={
            locations
          }
          placeholder="All Locations"
          onChange={
            onLocationChange
          }
        />


        {/* ========================================================
            SHIFT
        ========================================================= */}

        <FilterDropdown
          label="Shift"
          value={
            selectedShift
          }
          options={
            shifts
          }
          placeholder="All Shifts"
          onChange={
            onShiftChange
          }
        />

      </div>

    </div>
  );
};

export default ProductivityFilters;